import React from 'react';
import { Calendar, MapPin, Users, Award } from 'lucide-react';

const Story = () => {
  const timeline = [
    {
      date: 'September 2023',
      title: 'Company Founded',
      description: 'CSS Caribbean Meal was founded as a family-owned food manufacturing facility in Newnan, Georgia, with a vision to bring authentic Caribbean flavors to families.',
      icon: Users
    },
    {
      date: 'March 13, 2024',
      title: 'First Production Launch',
      description: 'We proudly launched our first production of Caribbean-style frozen dinners, marking the start of our journey in delivering quality meals.',
      icon: Award
    },
    {
      date: 'Today & Beyond',
      title: 'Growing Reach',
      description: 'Currently serving families across the US with plans to expand globally soon, bringing authentic Caribbean meals to every family.',
      icon: MapPin
    }
  ];

  return (
    <section id="story" className="story-section">
      <div className="story-container">
        <div className="story-header">
          <span className="section-badge">About Us</span>
          <h2 className="section-title">A Family Story of Passion & Authenticity</h2>
          <p className="section-description">
            CSS Caribbean Meal is a family-owned food manufacturing facility located in Newnan, Georgia. 
            Founded in September 2023, we are a small facility producing frozen dinners, Caribbean style. 
            Focusing on bringing authentic Caribbean meals to every family across the US, and globally soon.
          </p>
        </div>

        <div className="timeline">
          {timeline.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="timeline-item" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="timeline-icon">
                  <Icon size={24} />
                </div>
                <div className="timeline-content">
                  <div className="timeline-date">
                    <Calendar size={16} />
                    <span>{item.date}</span>
                  </div>
                  <h3 className="timeline-title">{item.title}</h3>
                  <p className="timeline-description">{item.description}</p>
                </div>
                {index < timeline.length - 1 && <div className="timeline-line"></div>}
              </div>
            );
          })}
        </div>

        <div className="leadership-section">
          <h3 className="leadership-title">Meet Our Founder</h3>
          <div className="leadership-card">
            <div className="leadership-image-wrapper">
              <img 
                src="https://customer-assets.emergentagent.com/job_authentic-meals/artifacts/axex5gjm_Picture%20of%20me%20copy.png" 
                alt="Carlene Smith Martin"
                className="leadership-image"
              />
            </div>
            <div className="leadership-info">
              <h4 className="leadership-name">Carlene Smith Martin</h4>
              <p className="leadership-title-position">President and CEO</p>
              <p className="leadership-company">CSS Caribbean Meal LLC</p>
              <div className="leadership-contact">
                <p className="leadership-address">
                  159 Greenfield Way<br />
                  Newnan, GA 30263
                </p>
                <p className="leadership-phone">(678) 876-9392</p>
              </div>
              <p className="leadership-motto">"Great Tasting Food Every Time!"</p>
            </div>
          </div>
          
          <div className="founder-story">
            <h4 className="founder-story-title">Meet Our Founder</h4>
            <div className="founder-story-content">
              <p>CSS Caribbean Meal LLC was born from a deep-rooted love for food, food that warms the soul and brings people together. From the lush hills of Highgate, St. Mary, Jamaica, to the vibrant community of Newnan, Georgia, founder Carlene Smith Martin has carried a lifelong passion: to cook meals that make people feel at home.</p>
              
              <p>As a child, Carlene was never far from the kitchen. Cooking wasn't just a chore; it was her hobby, it was her joy, her gift, and her way of connecting with others. Whether it was a pot of curry chicken bubbling on the stove or the aroma of jerk seasoning wafting through the air, her meals were always a celebration of flavor and culture. People didn't just eat her food; they remembered it.</p>
              
              <p>Feeding people is more than a profession for Carlene; it's a calling. Over the years, she poured her energy, creativity, and heritage into every dish. After years of cooking for friends, family, and community, she knew it was time to share her food on a bigger scale.</p>
              
              <p>That's how CSS Caribbean Meal LLC came to life, a company dedicated to delivering authentic, flavorful Jamaican cuisine to dinner tables across the U.S. and, eventually, the world.</p>
              
              <p>From perfectly spiced oxtail to tender curry chicken, from rice and peas to rich, comforting stews, every frozen meal is made with care, tradition, and the same love that Carlene puts into her very first dish. The goal is simple: to bring "Great Tasting Food Every Time!"</p>
              
              <p>Today, CSS Caribbean Meal LLC focuses on producing high-quality frozen Caribbean meals that taste homemade, because they are made the way Caribbean food should be bold, soulful, and unforgettable. Every bite tells a story of heritage, resilience, and a love for good food that started in a small Jamaican kitchen and now reaches homes across America.</p>
              
              <p className="founder-story-tagline">Let the flavors speak for themselves one meal at a time.</p>
              
              <h5 className="founder-story-subtitle">Background & Experience</h5>
              
              <p>Carlene migrated to the US to attend Prairie View A&M University, where she studied Animal Science and earned two degrees, a Bachelor of Science (Suma Cum Laude) and a Master of Science in Animal Science. Carlene was fortunate to secure a job with Cargill Inc. within her last semester (Fall 2000) at Prairie View. Carlene began her career in the US in January 2001 as a management Trainee at Cargill Inc. and served in multiple roles for approximately 11 years. She later moved on to other companies, such as Smithfield Foods, Kellogg, US Foods, and Dart Container.</p>
              
              <p>Carlene was always a high-energy, "fire in the belly" type of leader, and she expected the same from her peers and direct reports. While Carlene spent more than two decades working for big companies in the US, she was always eager to learn and was able to transpire some of those learnings into launching her own company.</p>
              
              <p>She spent her entire career protecting and safeguarding lives. There was always a focus on Food Safety, People's safety, and Environmental protection. In every role, the most important thing is to ensure that everyone understands the "why?"</p>
              
              <p>Building the capability of the people in leadership was of paramount importance to Carlene.</p>
            </div>
          </div>
        </div>

        <div className="story-mission">
          <div className="mission-card">
            <h3>Our Purpose</h3>
            <p>Feeding families with nutritious meals in a convenient way.</p>
          </div>
          <div className="mission-card">
            <h3>Our Craftsmanship</h3>
            <p>Our carefully crafted products are hand-trimmed and prepared to specifications, ensuring quality in every bite.</p>
          </div>
          <div className="mission-card">
            <h3>Our Standards</h3>
            <p>Our products are produced under USDA Food Safety guidelines in addition to CSS above and beyond Food Safety and Quality requirements.</p>
          </div>
        </div>
        
        <div className="food-safety-section">
          <div className="safety-content">
            <h3>Food Safety is a Chain</h3>
            <p>Our products are labeled with handling instructions to ensure products are maintained to protect the integrity and safety of the products. Food safety is a chain; therefore, everyone must play their part to ensure product safety and quality.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Story;
